import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CardSearch from '../pages/CardSearch';
import CalculatorModal from '../components/CalculatorModal';
import './YuGiOhTimer.css';


const YuGiOhTimer = () => {
  const { t } = useTranslation();

  const [initialTime, setInitialTime] = useState(600); // default 10 min
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [running, setRunning] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [showCardSearch, setShowCardSearch] = useState(false);
  const [showInitialConfig, setShowInitialConfig] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [player1LP, setPlayer1LP] = useState(8000);
  const [player2LP, setPlayer2LP] = useState(8000);
  const [player1Input, setPlayer1Input] = useState("");
  const [player2Input, setPlayer2Input] = useState("");
  const [player1Operator, setPlayer1Operator] = useState("+");
  const [player2Operator, setPlayer2Operator] = useState("+");

  const toggleOperator = (player) => {
    if (player === 1) {
      setPlayer1Operator((prev) => (prev === "+" ? "-" : "+"));
    } else {
      setPlayer2Operator((prev) => (prev === "+" ? "-" : "+"));
    }
  };

  const updateLP = (player) => {
    if (player === 1) {
      const amount = parseInt(player1Input) || 0;
      setPlayer1LP((prev) =>
        player1Operator === "+" ? prev + amount : Math.max(0, prev - amount)
      );
      setPlayer1Input("");
    } else {
      const amount = parseInt(player2Input) || 0;
      setPlayer2LP((prev) =>
        player2Operator === "+" ? prev + amount : Math.max(0, prev - amount)
      );
      setPlayer2Input("");
    }
  };




  // Load saved state from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('ygoTimerState'));
    const pausedByClose = localStorage.getItem('ygoTimerPausedByClose') === 'true';
    const lastTimestamp = localStorage.getItem('ygoLastTimestamp');

    if (saved && saved.timerStarted) {
      setInitialTime(saved.initialTime);

      if (lastTimestamp && saved.running && !pausedByClose) {
        const now = Date.now();
        const elapsed = Math.floor((now - Number(lastTimestamp)) / 1000);
        const adjustedTime = Math.max(saved.timeLeft - elapsed, 0);
        setTimeLeft(adjustedTime);
      } else {
        setTimeLeft(saved.timeLeft);
      }

      setRunning(pausedByClose ? false : saved.running);
      setTimerStarted(saved.timerStarted);
    } else {
      setShowInitialConfig(true);
    }

    localStorage.removeItem('ygoTimerPausedByClose');
  }, []);
  useEffect(() => {
    window.setCardSearchOpen?.(showCardSearch);
  }, [showCardSearch]);

  useEffect(() => {
    if (showCardSearch) {
      document.body.classList.add('card-search-open');
    } else {
      document.body.classList.remove('card-search-open');
    }
  }, [showCardSearch]);

  // Save current timer state
  useEffect(() => {
    const state = {
      initialTime,
      timeLeft,
      running,
      timerStarted,
    };
    localStorage.setItem('ygoTimerState', JSON.stringify(state));
  }, [initialTime, timeLeft, running, timerStarted]);

  // Save timestamp for elapsed time tracking
  useEffect(() => {
    if (running) {
      localStorage.setItem('ygoLastTimestamp', Date.now().toString());
    }
  }, [running, timeLeft]);

  // Countdown interval with timestamp correction
  useEffect(() => {
    let interval = null;

    if (running && timeLeft > 0) {
      setLastUpdatedAt(Date.now());

      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - lastUpdatedAt) / 1000);
        setTimeLeft((prev) => Math.max(prev - elapsed, 0));
        setLastUpdatedAt(now);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [running, lastUpdatedAt]);

  // Adjust on tab visibility change (if needed)
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && running && lastUpdatedAt) {
        const now = Date.now();
        const elapsed = Math.floor((now - lastUpdatedAt) / 1000);
        setTimeLeft((prev) => Math.max(prev - elapsed, 0));
        setLastUpdatedAt(now);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [running, lastUpdatedAt]);

  // Pause timer if app is closed or refreshed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (running) {
        localStorage.setItem('ygoTimerPausedByClose', 'true');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [running]);

  useEffect(() => {
    if (!timerStarted && showInitialConfig) {
      window.setShowGlobalTopButtons?.(true);
    } else {
      window.setShowGlobalTopButtons?.(false);
    }
  }, [timerStarted, showInitialConfig]);

  useEffect(() => {
    if (!timerStarted && showInitialConfig) {
      window.setShowGlobalMuteButton?.(true);
    } else {
      window.setShowGlobalMuteButton?.(false);
    }
  }, [timerStarted, showInitialConfig]);





  const reset = () => {
    setRunning(false);
    setTimeLeft(initialTime);
    setTimerStarted(false);
    setShowInitialConfig(true);
    localStorage.removeItem('ygoTimerState');
    localStorage.removeItem('ygoTimerPausedByClose');
    localStorage.removeItem('ygoLastTimestamp');
  };

  return (
    <div className="ygo-timer-container">
      {!timerStarted && showInitialConfig ? (
        <div className="timer-config">
          <h3>{t('timeLimit.selectTime')}</h3>
          <div className="time-options">
            {[1200, 2100, 2400, 2700].map((sec) => (
              <button
                key={sec}
                className={`timer-select-btn ${initialTime === sec ? 'selected' : ''}`}
                onClick={() => {
                  setInitialTime(sec);
                  setTimeLeft(sec);
                }}
              >
                {sec / 60} min
              </button>
            ))}
          </div>
          <button
            className="timer-start-btn"
            onClick={() => {
              setTimerStarted(true);
            }}
          >
            {t('timeLimit.startDuel')}
          </button>
        </div>
      ) : (
        <div className="ygo-timer-clock">
          <div className="ygo-time-display">
            {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
          </div>
{/* 
          <div className="lp-panel">
            <div>Player 1 LP: {player1LP}</div>
            <input
              type="number"
              value={player1Input}
              onChange={(e) => setPlayer1Input(e.target.value)}
            />
            <button className="toggle-btn" onClick={() => toggleOperator(1)}>{player1Operator}</button>
            <button onClick={() => updateLP(1)}>Update LP</button>
          </div>

          <div className="lp-panel">
            <div>Player 2 LP: {player2LP}</div>
            <input
              type="number"
              value={player2Input}
              onChange={(e) => setPlayer2Input(e.target.value)}
            />
            <button onClick={() => toggleOperator(2)}>{player2Operator}</button>
            <button onClick={() => updateLP(2)}>Update LP</button>
          </div> */}

          <div className="ygo-timer-controls">
            <button
              className="ygo-timer-btn play"
              onClick={() => setRunning(!running)}
            >
              {running ? t('common.pause') : t('common.start')}
            </button>
            <button
              className="ygo-timer-btn reset"
              onClick={reset}
            >
              {t('common.reset')}
            </button>
            <button onClick={() => setShowCalculator(true)}>
              Open Calculator
            </button>
            <button onClick={() => setShowCardSearch(true)}>Search Card</button>
          </div>
        </div>
      )}

      {showCardSearch && (
        <div className="card-search-modal-overlay" onClick={() => setShowCardSearch(false)}>
          <div className="card-search-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-card-search" onClick={() => setShowCardSearch(false)}>✕</button>
            <CardSearch />
          </div>
        </div>
      )}

      <CalculatorModal show={showCalculator} onClose={() => setShowCalculator(false)} />
    </div>
  );

};

export default YuGiOhTimer;
